import axios from 'axios';
import { API_CONFIG } from '../constants/index.js';

/**
 * Question Service
 * Handles all Q&A related API operations
 * Uses the global axios interceptors from AuthService for authentication
 */
class QuestionService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
  }

  /**
   * Get all questions for a project
   * @param {string} slug - Project slug
   * @param {object} options - Query options (limit, offset)
   * @returns {Promise<object>} Questions data
   */
  async getProjectQuestions(slug, options = {}) {
    try {
      const { limit = 10, offset = 0 } = options;
      const response = await axios.get(`${this.baseURL}/api/projects/${slug}/questions`, {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get question statistics for a project
   * @param {string} slug - Project slug
   * @returns {Promise<object>} Question statistics
   */
  async getQuestionStats(slug) {
    try {
      const response = await axios.get(`${this.baseURL}/api/projects/${slug}/questions/stats`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create a new question
   * @param {string} slug - Project slug
   * @param {string} question - Question text
   * @returns {Promise<object>} Created question data
   */
  async createQuestion(slug, question) {
    try {
      const response = await axios.post(`${this.baseURL}/api/projects/${slug}/questions`, {
        question
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Answer a question (project owner only)
   * @param {string} questionId - Question ID
   * @param {string} answer - Answer text
   * @returns {Promise<object>} Answer data
   */
  async answerQuestion(questionId, answer) {
    try {
      const response = await axios.post(`${this.baseURL}/api/questions/${questionId}/answer`, {
        answer
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle pin status of a question (project owner only)
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Pin status result
   */
  async togglePinQuestion(questionId) {
    try {
      const response = await axios.put(`${this.baseURL}/api/questions/${questionId}/pin`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Hide a question (project owner only)
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Hide result
   */
  async hideQuestion(questionId) {
    try {
      const response = await axios.put(`${this.baseURL}/api/questions/${questionId}/hide`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete a question (user can delete their own unanswered question)
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Deletion result
   */
  async deleteQuestion(questionId) {
    try {
      const response = await axios.delete(`${this.baseURL}/api/questions/${questionId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete an answer (project owner only)
   * @param {string} questionId - Question ID
   * @returns {Promise<object>} Deletion result
   */
  async deleteAnswer(questionId) {
    try {
      const response = await axios.delete(`${this.baseURL}/api/questions/${questionId}/answer`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {object} error - Axios error object
   * @returns {object} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response;
      return {
        message: data?.error?.message || 'An error occurred',
        code: data?.error?.code || 'UNKNOWN_ERROR',
        status,
        details: data?.error?.details
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0
      };
    }
  }
}

// Create and export singleton instance
const questionService = new QuestionService();
export default questionService;
